import { useMemo } from 'react';
import { isUndefined, orderBy } from 'lodash';
import {
  NAME_SHEET_FUND_KEY,
  NAME_SHEET_RELATED_ENTITY_KEY,
  RELATED_ENTITY_OWNER_VALUE,
} from 'pages/Waterfall/constants';
import { UseFundInvestmentSheetConfigProps } from './useFundInvestmentSheetConfig.props';
import { FundShare } from './useFundInvestmentSheetConfig.types';
import { SpreadsheetConfig } from '../../../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import fundInvestmentParser from '../FundInvestmentParser/fundInvestmentParser';
import fundInvestmentRowConfig from '../FundInvestmentRowConfig/fundInvestmentRowConfig';

const useFundInvestmentSheetConfig = ({
  weightedShareValues,
  validSecurities,
  idToAssociateTo,
}: UseFundInvestmentSheetConfigProps) => {
  const fundShares = useMemo<FundShare[]>(() => {
    if (weightedShareValues && validSecurities) {
      const tmpShareValues = weightedShareValues.map(sv => sv.share_values);
      const tmpWeightedShareValues = [...weightedShareValues];

      const securitiesOrder = new Map(validSecurities.map((sec, index) => [sec.id, index]));

      // Some funds may not have investments in all securities
      const allShareValuesSameSize =
        tmpShareValues.every(item => item.length === tmpShareValues[0].length) &&
        validSecurities.length &&
        tmpShareValues.length &&
        validSecurities.length === tmpShareValues[0].length;

      if (!allShareValuesSameSize) {
        // Identify missing securities by fund and fill in with zeroes
        tmpWeightedShareValues.forEach(sv => {
          const securitiesByFund = sv.share_values.map(s => s.securityId);
          validSecurities.forEach(sec => {
            if (!securitiesByFund.includes(sec.id)) {
              sv.share_values.push({
                securityId: sec.id,
                security: sec.name,
                per_share: 0,
                shares: 0,
              });
            }
          });
        });
      }
      return weightedShareValues.map(({ fund, share_values, owner }) => ({
        fund,
        owner,
        // make order consistent with left side table
        share_values: orderBy(
          share_values
            ?.filter(sv => !isUndefined(sv.per_share))
            .map(sv => ({ ...sv, securityOrder: securitiesOrder.get(sv.securityId as number) })),
          'securityOrder',
          'asc'
        ).map(sv => {
          const { securityOrder, ...rest } = sv;
          return rest;
        }),
      }));
    }
    return [];
  }, [weightedShareValues, validSecurities]);

  const fundSharesConfigs = useMemo(() => {
    if (!fundShares || fundShares.length === 0) {
      return [];
    }
    return fundShares.map(fundShare => {
      const { fund, owner } = fundShare;
      const endSuffix = owner === RELATED_ENTITY_OWNER_VALUE ? NAME_SHEET_RELATED_ENTITY_KEY : NAME_SHEET_FUND_KEY;
      const sheetNameSuffix = idToAssociateTo
        ? `${fund.id}_${idToAssociateTo}_${endSuffix}`
        : `${fund.id}_${endSuffix}`;
      return new SpreadsheetConfig({
        columns: [{ label: 'Share Count' }, { label: 'Value' }],
        rowConfig: fundInvestmentRowConfig({ fundShare }),
        parser: fundInvestmentParser,
        totalParser: () => ({}) as unknown as SpreadsheetConfig['totalParser'],
        tableData: {} as unknown as SpreadsheetConfig['tableData'],
        currencyFormatter: () => '' as unknown as SpreadsheetConfig['currencyFormatter'],
        unitsFormatter: () => '' as unknown as SpreadsheetConfig['unitsFormatter'],
        format: () => '' as unknown as SpreadsheetConfig['format'],
        showTitlesColumn: false,
        name: `FundInvestment_${sheetNameSuffix}`,
      });
    });
  }, [fundShares, idToAssociateTo]);

  return { spreadsheets: fundSharesConfigs };
};

export default useFundInvestmentSheetConfig;
